import { useState } from 'react'
import { api, GateState } from '../api'
import { theme } from '../theme'
import { Button, Card, Page } from '../components/ui'
import { explainSignInProblem, signInWithGoogle } from '../lib/googleSignIn'
import { fromServer, t } from '../lib/i18n'

/**
 * The only screen a parent must get past, and the only one a child never sees.
 *
 * It draws itself from what the server said. On a laptop the server is in
 * 'local' mode and the button signs in with no password at all; in the cloud
 * it is 'google' and the button opens Google's window, collects an ID token
 * and sends it up to be verified. One bundle, both behaviours, and the server
 * decides — because the server is the only thing that knows.
 *
 * Nothing is decided here. The browser obtains a token; the server checks the
 * signature with Google and then checks the address against a list it keeps
 * to itself. A parent whose account is not on that list gets a clear sentence
 * saying so, and there is nothing they can do in this window to change it.
 */
/**
 * The sign-in action on its own, so the front page's button and this screen's
 * button are the same code path. Two copies of sign-in is how one of them
 * ends up skipping a check.
 */
export function useSignIn(gate: GateState | null, onDone: () => void) {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const google = gate?.auth_mode === 'google'
  const config = gate?.firebase ?? null

  const go = async () => {
    setBusy(true)
    setProblem(null)
    try {
      if (google) {
        if (!config) {
          // The server said 'google' and sent no configuration. That is a
          // deployment mistake, not something a parent can fix, so say so
          // plainly rather than offering "try again".
          setProblem(t('Sign-in is not finished being set up on the server.'))
          return
        }
        const token = await signInWithGoogle(config)
        await api.signIn(token)
      } else {
        await api.signIn()
      }
      onDone()
    } catch (error) {
      // Two quite different failures arrive here. Google's own (window
      // closed, pop-ups blocked, no network) carry a code and get a specific
      // sentence. Ours — above all "that account is not set up" — arrive as
      // a 401 whose message was written for a parent to read, so it is shown
      // as it is rather than replaced with something vaguer.
      // In the language picked on the front page (round 14).
      setProblem(fromServer(error, explainSignInProblem(error)))
    } finally {
      setBusy(false)
    }
  }

  return { go, busy, problem, google }
}

export function SignIn({ gate, onDone }: { gate: GateState | null; onDone: () => void }) {
  const { go, busy, problem, google } = useSignIn(gate, onDone)

  return (
    <Page width={420}>
      <div style={{ textAlign: 'center', padding: '48px 0 28px' }}>
        <div style={{ fontSize: 52 }}>🌱</div>
        <h1 style={{ margin: '10px 0 4px', fontSize: 30, fontWeight: 800, color: theme.text }}>
          Word Sprout
        </h1>
        <p style={{ margin: 0, color: theme.muted, fontSize: 16 }}>
          English practice, a little every day.
        </p>
      </div>

      <Card>
        <p style={{ margin: '0 0 18px', color: theme.text2, fontSize: 16, lineHeight: 1.55 }}>
          A grown-up signs in. Children do not need their own account.
        </p>
        <Button full onClick={go} disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in with Google'}
        </Button>
        {problem ? (
          <p style={{ margin: '14px 0 0', color: theme.red, fontSize: 15, lineHeight: 1.5 }}>
            {problem}
          </p>
        ) : null}
        <p style={{ margin: '16px 0 0', color: theme.faint, fontSize: 13, lineHeight: 1.5 }}>
          {google
            ? 'Only accounts the grown-up has added can sign in.'
            : 'Running locally, so this signs you in without a password. The button becomes real Google Sign-In in the cloud.'}
        </p>
      </Card>
    </Page>
  )
}
